import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math'; 
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import EnhancedMarkdownEditor from './Editor';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { 
  solarizedlight, 
  vscDarkPlus, 
  dracula, 
  tomorrow,
  materialDark,
  materialLight,
  oneDark,
  oneLight,
  nightOwl,
  nord
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { 
  FaDownload, 
  FaTrashAlt, 
  FaSearch, 
  FaCopy, 
  FaEdit,
  FaEarlybirds,
  FaProjectDiagram, 
  FaInfoCircle,
  FaSignOutAlt,
  FaVolumeUp,
  FaPlay,
  FaPause,
  FaPalette,
  FaBars,
  FaTimes,
  FaMoon,
  FaCode,
  FaPaintBrush
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './MainPage.css';
import MarkdownAssistant from './MarkdownAssistant';
import AboutModal from './AboutModal';
import GraphView from './GraphView';
import AlertModal from './AlertModal';

// Create a custom Tokyo Night theme for code blocks
const tokyoNightTheme = {
  'code[class*="language-"]': {
    color: '#a9b1d6',
    background: '#1a1b26',
    fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    textAlign: 'left',
    whiteSpace: 'pre',
    wordSpacing: 'normal',
    wordBreak: 'normal',
    wordWrap: 'normal',
    lineHeight: '1.5',
    tabSize: '4',
    hyphens: 'none',
  },
  'pre[class*="language-"]': {
    color: '#a9b1d6',
    background: '#1a1b26',
    fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    textAlign: 'left',
    whiteSpace: 'pre',
    wordSpacing: 'normal',
    wordBreak: 'normal',
    wordWrap: 'normal',
    lineHeight: '1.5',
    tabSize: '4',
    hyphens: 'none',
    padding: '1em',
    margin: '0.5em 0',
    overflow: 'auto',
  },
  'comment': {
    color: '#565f89'
  },
  'prolog': {
    color: '#565f89'
  },
  'doctype': {
    color: '#565f89'
  },
  'cdata': {
    color: '#565f89'
  },
  'punctuation': {
    color: '#a9b1d6'
  },
  '.namespace': {
    opacity: 0.7
  },
  'property': {
    color: '#7aa2f7'
  },
  'tag': {
    color: '#7aa2f7'
  },
  'boolean': {
    color: '#ff9e64'
  },
  'number': {
    color: '#ff9e64'
  },
  'constant': {
    color: '#bb9af7'
  },
  'symbol': {
    color: '#bb9af7'
  },
  'deleted': {
    color: '#f7768e'
  },
  'selector': {
    color: '#9ece6a'
  },
  'attr-name': {
    color: '#7aa2f7'
  },
  'string': {
    color: '#9ece6a'
  },
  'char': {
    color: '#9ece6a'
  },
  'builtin': {
    color: '#7aa2f7'
  },
  'inserted': {
    color: '#9ece6a'
  },
  'operator': {
    color: '#89ddff'
  },
  'entity': {
    color: '#7aa2f7',
    cursor: 'help'
  },
  'url': {
    color: '#7dcfff'
  },
  '.language-css .token.string': {
    color: '#9ece6a'
  },
  '.style .token.string': {
    color: '#9ece6a'
  },
  'variable': {
    color: '#bb9af7'
  },
  'atrule': {
    color: '#7aa2f7'
  },
  'attr-value': {
    color: '#9ece6a'
  },
  'function': {
    color: '#7aa2f7'
  },
  'class-name': {
    color: '#7aa2f7'
  },
  'keyword': {
    color: '#9d7cd8'
  },
  'regex': {
    color: '#b4f9f8'
  },
  'important': {
    color: '#ff9e64',
    fontWeight: 'bold'
  },
  'bold': {
    fontWeight: 'bold'
  },
  'italic': {
    fontStyle: 'italic'
  }
};

const MainPage = () => {
  // First define states
  const [markdownFiles, setMarkdownFiles] = useState([]);
  const [newMarkdown, setNewMarkdown] = useState({ title: '', content: '' });
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('theme') || 'shinkiro';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showGraphView, setShowGraphView] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [t, setT] = useState(false);
  // Add refs for preview and editor sections
  const editorRef = useRef(null);
  const previewRef = useRef(null);
  const previewContentRef = useRef(null);

  // Then define themes object using currentTheme
  const themes = {
    'Shinkiro': currentTheme?.includes('dark') ? materialDark : tomorrow,
    'Solarized Light': solarizedlight,
    'VSCode Dark': vscDarkPlus,
    'Dracula': dracula,
    'Material Dark': materialDark,
    'Material Light': materialLight,
    'One Dark': oneDark,
    'One Light': oneLight,
    'Night Owl': nightOwl,
    'Nord': nord,
    'Tokyo Night': tokyoNightTheme
  };
  
  // TTS state
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsVoices, setTtsVoices] = useState([]);
  const [ttsVoice, setTtsVoice] = useState(null);
  const [ttsRate, setTtsRate] = useState(1);
  const [ttsPitch, setTtsPitch] = useState(1);
  const [showTTSControls, setShowTTSControls] = useState(false);
  
  const [codeTheme, setCodeTheme] = useState(() => {
    const savedTheme = localStorage.getItem('codeTheme');
    if (savedTheme && themes[savedTheme]) {
      return themes[savedTheme];
    }
    return themes['Shinkiro'];
  });
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  
  const [previewTheme, setPreviewTheme] = useState(() => {
    const savedPreviewTheme = localStorage.getItem('previewTheme');
    return savedPreviewTheme || 'shinkiro';
  });
  
  const navigate = useNavigate();
  
  // Add state for drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Add scroll handlers
  const scrollToEditor = () => {
    editorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPreview = () => {
    previewRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  
  useEffect(() => {
    fetchMarkdownFiles();
  }, []);
  
  useEffect(() => {
    localStorage.setItem('theme', currentTheme);
    document.body.className = `theme-${currentTheme}`;
  }, [currentTheme]);

  // Load available TTS voices
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        setTtsVoices(voices);
        if (voices.length > 0 && !ttsVoice) {
          setTtsVoice(voices[0]);
        }
      };
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, [ttsVoice]);

  useEffect(() => {
    const currentThemeName = Object.keys(themes).find(key => themes[key] === codeTheme);
    localStorage.setItem('codeTheme', currentThemeName);
  }, [codeTheme, themes]);

  useEffect(() => {
    localStorage.setItem('previewTheme', previewTheme);
  }, [previewTheme]);

  const fetchMarkdownFiles = async () => {
    try {
      const response = await axios.get('https://shinkiro-backend.vercel.app/api/markdown');
      setMarkdownFiles(response.data);
    } catch (error) {
      console.error('Error fetching markdown files', error);
    }
  };

  const handleChange = (e) => {
    setNewMarkdown({ ...newMarkdown, [e.target.name]: e.target.value });
  };

  const handleMarkdownAssist = (text) => {
    const textarea = document.querySelector('.textarea-field');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const content = newMarkdown.content;
    
    const newContent = content.substring(0, start) + text + content.substring(end);
    
    setNewMarkdown({ ...newMarkdown, content: newContent });
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  const handleKeyboardShortcuts = (e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          handleMarkdownAssist('**Bold**');
          break;
        case 'i':
          e.preventDefault();
          handleMarkdownAssist('*Italic*');
          break;
        case 'k':
          e.preventDefault();
          handleMarkdownAssist('[](url)');
          break;
        case 's':
          e.preventDefault();
          handleSubmit(e);
          break;
        default:
          break;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.delete(`https://shinkiro-backend.vercel.app/api/markdown/${editingId}`);
        setEditingId(null);
      }
  
      await axios.post('https://shinkiro-backend.vercel.app/api/markdown', newMarkdown);
      setNewMarkdown({ title: '', content: '' });
      if(editingId){
        showAlert('Note Updated successfully!');
      }else{
        showAlert('Note Created successfully!');
      }
      fetchMarkdownFiles();
    } catch (error) {
      console.error('Error submitting markdown', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await axios.delete(`https://shinkiro-backend.vercel.app/api/markdown/${id}`);
        setNewMarkdown({ title: '', content: '' });
        setEditingId(null);
        showAlert('Note Deleted successfully!');
        fetchMarkdownFiles();
      } catch (error) {
        console.error('Error deleting markdown', error);
      }
    }
  };

  const handleEdit = (markdown) => {
    setNewMarkdown({ title: markdown.title, content: markdown.content });
    setEditingId(markdown._id);
    // Add slight delay to ensure state updates before scrolling
    setTimeout(() => {
      previewRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleDownload = (markdown) => {
    const element = document.createElement('a');
    const file = new Blob([markdown.content], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = `${markdown.title}.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleTTS = () => {
    if (!ttsPlaying) {
      if ('speechSynthesis' in window) {
        const textToSpeak = previewContentRef.current ? previewContentRef.current.innerText : newMarkdown.content;
        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        if (ttsVoice) {
          utterance.voice = ttsVoice;
        }
        utterance.rate = ttsRate;
        utterance.pitch = ttsPitch;
        utterance.onend = () => setTtsPlaying(false);
        window.speechSynthesis.speak(utterance);
        setTtsPlaying(true);
      } else {
        alert("Your browser does not support text-to-speech.");
      }
    } else {
      window.speechSynthesis.cancel();
      setTtsPlaying(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuth');
    window.location.reload();
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const filteredFiles = markdownFiles.filter(file =>
    file.title.toLowerCase().includes(searchQuery.toLowerCase())
  ).reverse();

  const showAlert = (message) => {
    setModalMessage(message);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className={`container ${currentTheme === 'shinkiro-dark' ? 'dark-mode' : ''}`}>
      <div className="header">
        <h1 className="header-title">
          Shinkiro
          <span> -   蜃気楼 (Mirage) - Markdown Sanctuary</span>
        </h1>
        <div className="header-controls">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button 
            className="btn icon-btn"
            onClick={() => setShowGraphView(!showGraphView)}
            title="Toggle Graph View"
          >
            <FaProjectDiagram />
          </button>
          <button 
            className="btn icon-btn"
            onClick={() => setShowAbout(true)}
            title="About"
          >
            <FaInfoCircle />
          </button>
          <button 
            className="btn icon-btn"
            onClick={() => setShowThemeSelector(!showThemeSelector)}
            title="Change Theme"
          >
            <FaPalette />
          </button>
          <button 
            className="btn icon-btn"
            onClick={handleLogout}
            title="Logout"
          >
            <FaSignOutAlt />
          </button>
          <button
            className="menu-button"
            onClick={toggleDrawer}
            title="Menu"
          >
            <FaBars />
          </button>
        </div>
      </div>

      <div className={`drawer-menu ${isDrawerOpen ? 'open' : ''}`}>
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="drawer-menu-buttons">
          <button 
            className="btn icon-btn"
            onClick={() => {
              setShowGraphView(!showGraphView);
              setIsDrawerOpen(false);
            }}
          >
            <FaProjectDiagram /> Graph View
          </button>
          <button 
            className="btn icon-btn"
            onClick={() => {
              setShowAbout(true);
              setIsDrawerOpen(false);
            }}
          >
            <FaInfoCircle /> About
          </button>
          <button 
            className="btn icon-btn"
            onClick={() => {
              setShowThemeSelector(!showThemeSelector);
              setIsDrawerOpen(false);
            }}
          >
            <FaPalette /> Theme
          </button>
          <button 
            className="btn icon-btn"
            onClick={handleLogout}
          >
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </div>

      <div className="main-content">
        {showGraphView && (
          <div className="graph-container">
            <GraphView markdownFiles={markdownFiles} onNodeClick={(file) => {
              handleEdit(file);
              setShowGraphView(false);
            }} />
          </div>
        )}
        
        <div className="sidebar">
          <button 
            className="btn new-note-btn" 
            onClick={() => {
              setNewMarkdown({ title: '', content: '' });
              setEditingId(null);
            }}
          >
            + New Note
          </button>
          <div className="recently-created">
            <h3>Recently Created/Modified:</h3>
          </div>
          <div className="notes-list">
            {filteredFiles.map(markdown => (
              <div 
                key={markdown._id} 
                className={`note-item ${editingId === markdown._id ? 'active' : ''}`}
                onClick={() => handleEdit(markdown)}
              >
                <h3>{markdown.title}</h3>
                <p>{markdown.content.substring(0, 50)}...</p>
              </div>
            ))}
          </div>
        </div>

        <div className="editor-container">
          <div className={`markdown-preview ${previewTheme}`} ref={previewRef}>
            <div className="preview-header" style={{ display: 'flex', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>
                Preview: {newMarkdown.title && newMarkdown.title}
              </h2>
              {newMarkdown.content && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button 
                    className="btn copy-raw-btn"onClick={scrollToEditor}
                    title="Jump to Editor"
                  >
                    <FaEdit /> Editor
                  </button>
                  <button 
                    className="btn copy-raw-btn" 
                    onClick={() => copyToClipboard(newMarkdown.content)}
                    title="Copy Raw Markdown"
                  >
                    <FaCopy /> Copy Raw
                  </button>
                  <button 
                    className="btn copy-raw-btn"
                    onClick={() => setShowTTSControls(prev => !prev)}
                    title="Toggle TTS Controls"
                  >
                    <FaVolumeUp /> TTS
                  </button>
                </div>
              )}
            </div>
            
            {showTTSControls && (
              <div className="tts-controls-panel" style={{ marginTop: '10px', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                <div className="tts-buttons" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button 
                    className="btn copy-raw-btn" 
                    onClick={handleTTS} 
                    title={ttsPlaying ? "Pause TTS" : "Play TTS"}
                  >
                    {ttsPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                  <button 
                    className="btn copy-raw-btn"
                    onClick={() => setShowTTSControls(false)}
                    title="Close TTS Controls"
                  >
                    Close
                  </button>
                </div>
                <div className="tts-settings" style={{ display: 'flex', gap: '20px', alignItems: 'center', marginTop: '10px' }}>
                  <label className='tts-stuff'>
                    Voice:
                    <select
                      value={ttsVoice ? ttsVoice.name : ''}
                      onChange={(e) => {
                        const selectedVoice = ttsVoices.find(voice => voice.name === e.target.value);
                        setTtsVoice(selectedVoice);
                      }}
                    >
                      {ttsVoices.map((voice, idx) => (
                        <option key={idx} value={voice.name}>
                          {voice.name} ({voice.lang})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className='tts-stuff'>
                    Rate:
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={ttsRate}
                      onChange={(e) => setTtsRate(Number(e.target.value))}
                    />
                    {ttsRate}
                  </label>
                  <label className='tts-stuff'>
                    Pitch:
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={ttsPitch}
                      onChange={(e) => setTtsPitch(Number(e.target.value))}
                    />
                    {ttsPitch}
                  </label>
                </div>
              </div>
            )}
            
            <div className="markdown-preview-content" ref={previewContentRef}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    return inline ? (
                      <code {...props}>{children}</code>
                    ) : (
                      <div className="code-block-container">
                        <button 
                          className="copy-btn"
                          onClick={() => copyToClipboard(String(children).replace(/\n$/, ''))}
                          title="Copy Code Block"
                        >
                          <FaCopy />
                        </button>
                        <SyntaxHighlighter
                          style={codeTheme}
                          language={match ? match[1] : 'text'}
                          PreTag="div"
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      </div>
                    );
                  },
                }}
              >
                {newMarkdown.content}
              </ReactMarkdown>
            </div>
          </div>

          <form onSubmit={handleSubmit} className={`markdown-form ${previewTheme}`} ref={editorRef}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ color: "#d580ff" }}>Editor</h4>
              <button 
                type="button"
                className="btn copy-raw-btn" 
                onClick={scrollToPreview}
                title="Jump to Preview"
              >
                <FaEarlybirds/> Preview
              </button>
            </div>
            
            <input
              type="text"
              name="title"
              value={newMarkdown.title}
              onChange={handleChange}
              placeholder="Note title"
              required
              className="input-field"
            />
            
            <MarkdownAssistant onInsert={handleMarkdownAssist} />
            
            <div className="editor-hints">
              <p>
                Keyboard Shortcuts: <strong>Ctrl+B</strong> for Bold, <strong>Ctrl+I</strong> for Italic, <strong>Ctrl+K</strong> for Link, <strong>Ctrl+S</strong> to Save.
              </p>
            </div>
            
            {/* <textarea
              name="content"
              value={newMarkdown.content}
              onChange={handleChange}
              onKeyDown={handleKeyboardShortcuts}
              placeholder="Start writing your markdown here... Use [[ ]] for internal links"
              required
              className="textarea-field"
            /> */}
            <EnhancedMarkdownEditor
              value={newMarkdown.content}
              onChange={(e) => setNewMarkdown({ ...newMarkdown, content: e.target.value })}
              placeholder="Start writing your markdown here... Use [[ ]] for internal links"
              existingNotes={markdownFiles}
              className="textarea-field"
              onKeyDown={handleKeyboardShortcuts}
            />
            
            <div className="button-row">
              <button type="submit" className="btn save-btn">
                {editingId ? 'Update Note' : 'Create Note'}
              </button>
              {editingId && (
                <>
                  <button 
                    type="button" 
                    className="btn delete-btn"
                    onClick={() => handleDelete(editingId)}
                  >
                    <FaTrashAlt /> Delete
                  </button>
                  <button 
                    type="button" 
                    className="btn download-btn"
                    onClick={() => handleDownload(newMarkdown)}
                  >
                    <FaDownload /> Download
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>

      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {showThemeSelector && (
        <div className="theme-selector">
          <div className="theme-header">
            <h3>Theme Settings :D</h3>
            <button 
              className="close-btn"
              onClick={() => setShowThemeSelector(false)}
            >
              <FaTimes />
            </button>
          </div>

          <div className="theme-group">
            <div className="theme-group-header">
              <FaPaintBrush /> Overall Theme
            </div>
            <select 
              value={currentTheme}
              onChange={(e) => setCurrentTheme(e.target.value)}
            >
              <optgroup label="Light Themes">
                <option value="shinkiro">Shinkiro Light</option>
                <option value="autumn">Autumn</option>
                <option value="ocean">Ocean</option>
                <option value="sakura">Sakura</option>
                <option value="forest">Forest</option>
                <option value="sunset">Sunset</option>
                <option value="arctic">Arctic</option>
                <option value="retro">Retro</option>
              </optgroup>
              <optgroup label="Dark Themes">
                <option value="shinkiro-dark">Shinkiro Dark</option>
                <option value="cyberpunk">Cyberpunk</option>
                <option value="matrix">Matrix</option>
                <option value="midnight">Midnight</option>
                <option value="tokyo-night">Tokyo Night</option>
              </optgroup>
            </select>
          </div>

          <div className="theme-group">
            <div className="theme-group-header">
              <FaCode /> Code Block Theme
            </div>
            <select 
              value={Object.keys(themes).find(key => themes[key] === codeTheme)}
              onChange={(e) => setCodeTheme(themes[e.target.value])}
            >
              <optgroup label="Light Themes">
                <option value="Shinkiro">Shinkiro</option>
                <option value="Solarized Light">Solarized Light</option>
                <option value="Material Light">Material Light</option>
                <option value="One Light">One Light</option>
              </optgroup>
              <optgroup label="Dark Themes">
                <option value="VSCode Dark">VSCode Dark</option>
                <option value="Dracula">Dracula</option>
                <option value="Material Dark">Material Dark</option>
                <option value="One Dark">One Dark</option>
                <option value="Night Owl">Night Owl</option>
                <option value="Nord">Nord</option>
                <option value="Tokyo Night">Tokyo Night</option>
              </optgroup>
            </select>
          </div>

          <div className="theme-group">
            <div className="theme-group-header">
              <FaPalette /> Editor & Preview Theme
            </div>
            <select 
              value={previewTheme}
              onChange={(e) => setPreviewTheme(e.target.value)}
            >
              <optgroup label="Light Themes">
                <option value="shinkiro">Shinkiro</option>
                <option value="github-light">GitHub Light</option>
                <option value="notion-light">Notion Light</option>
                <option value="solarized-light">Solarized Light</option>
                <option value="material-light">Material Light</option>
              </optgroup>
              <optgroup label="Dark Themes">
                <option value="github-dark">GitHub Dark</option>
                <option value="notion-dark">Notion Dark</option>
                <option value="dracula">Dracula</option>
                <option value="nord">Nord</option>
                <option value="solarized-dark">Solarized Dark</option>
                <option value="one-dark">One Dark</option>
                <option value="material-dark">Material Dark</option>
                <option value="tokyo-night">Tokyo Night</option>
              </optgroup>
            </select>
          </div>
        </div>
      )}

      {isModalOpen && (
        <AlertModal message={modalMessage} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default MainPage;